import { PopoverPosition } from '../types';
import { useCallback, useEffect, useRef, useState } from 'react';

export function usePopover(opened: boolean, position: PopoverPosition, onClose: () => void) {
  const popoverRef = useRef<HTMLDivElement>(null);

  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [arrowSide, setArrowSide] = useState<'left' | 'right' | 'none'>('left');
  const [popoverWidth, setPopoverWidth] = useState(400);

  // Calculate and update popover position
  const calculatePosition = useCallback(() => {
    if (!opened || !popoverRef.current) return;

    const popover = popoverRef.current;
    const rect = popover.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 768;
    const mobileWidth = Math.min(viewportWidth - 32, 400); // 16px padding on each side

    // Set responsive width
    setPopoverWidth(isMobile ? mobileWidth : 400);

    let { x, y } = position;
    let side: 'left' | 'right' | 'none' = 'left';

    // On mobile or very narrow screens, center the popover or position it better
    if (isMobile) {
      // On mobile, try to position relative to event, but ensure it fits
      if (position.eventElement) {
        const eventRect = position.eventElement.getBoundingClientRect();
        const eventCenterX = eventRect.left + eventRect.width / 2;

        // Try to center on event, but keep within viewport
        x = Math.max(16, Math.min(eventCenterX - mobileWidth / 2, viewportWidth - mobileWidth - 16));

        // If popover is too wide or event is near edge, center it on screen
        if (mobileWidth >= viewportWidth - 32 || eventRect.left < 50 || eventRect.right > viewportWidth - 50) {
          x = (viewportWidth - mobileWidth) / 2;
          side = 'none'; // Hide arrow on very narrow screens or when centered
        } else {
          // Show arrow pointing to event
          const arrowOffset = eventCenterX - x;
          if (arrowOffset < 20) {
            side = 'left';
          } else if (arrowOffset > mobileWidth - 20) {
            side = 'right';
          } else {
            side = 'none'; // Hide arrow if event is in middle of popover
          }
        }
      } else {
        // Center on screen if no event element
        x = (viewportWidth - mobileWidth) / 2;
        side = 'none';
      }
    } else {
      // Desktop behavior - position to the right of event
      if (x + rect.width > viewportWidth) {
        // Position to the left of the event instead
        if (position.eventElement) {
          const eventRect = position.eventElement.getBoundingClientRect();
          x = eventRect.left - rect.width - 12;
          side = 'right';
        } else {
          x = viewportWidth - rect.width - 16;
        }
      }

      // Ensure popover doesn't go off left edge
      if (x < 16) {
        x = 16;
        side = 'none';
      }
    }

    // Adjust vertical position if popover would go off-screen
    if (y + rect.height / 2 > viewportHeight) {
      y = viewportHeight - rect.height - 16;
    } else if (y - rect.height / 2 < 0) {
      y = rect.height / 2 + 16;
    }

    setArrowSide(side);
    setAdjustedPosition({ x, y, eventElement: position.eventElement });
  }, [opened, position]);

  // Adjust position to keep popover in viewport
  useEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  // Recalculate on window resize
  useEffect(() => {
    if (!opened) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      // Clear any pending timeout to avoid race conditions
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Small delay to ensure DOM has updated
      timeoutId = setTimeout(() => {
        calculatePosition();
        timeoutId = null;
      }, 0);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [opened, calculatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!opened) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        // Don't close if clicking on the event element
        if (position.eventElement && position.eventElement.contains(e.target as Node)) {
          return;
        }

        // Don't close if clicking inside a Mantine popover or menu dropdown
        const target = e.target as Element;
        if (target.closest?.('.mantine-Popover-dropdown') || target.closest?.('.mantine-Menu-dropdown')) {
          return;
        }

        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Use setTimeout to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [opened, onClose, position.eventElement]);

  return {
    arrowSide,
    popoverRef,
    popoverWidth,
    adjustedPosition,
  };
}
